type ChapterData = {
  id: string
  title: string
  children: string[]
}

const structureScrollReport = (): void => {
  const article = document.querySelector<HTMLElement>('.editorial-prototype .editorial-article')
  if (!article || article.querySelector('.editorial-chapter')) return

  const boundary = article.querySelector('.article-pager')
  const candidates = Array.from(article.children)
  const contentNodes = boundary
    ? candidates.slice(0, candidates.indexOf(boundary))
    : candidates

  let section: HTMLElement | null = null
  let content: HTMLElement | null = null

  const createSection = (modifier: string): void => {
    section = document.createElement('section')
    section.className = `editorial-chapter ${modifier}`.trim()
    content = document.createElement('div')
    content.className = 'editorial-chapter__content'
    section.append(content)
    article.insertBefore(section, boundary)
  }

  contentNodes.forEach((node) => {
    if (node.tagName === 'H3') {
      createSection('editorial-chapter--section')
      section?.setAttribute('data-report-chapter', (node as HTMLElement).id)
    } else if (!section) {
      createSection('editorial-chapter--intro')
    }
    content?.append(node)
  })
}

/**
 * Keeps the compact chapter switcher synchronized with the section currently
 * crossing the reading line.
 */
export function initEditorialReading(): void {
  structureScrollReport()

  const raw = document.getElementById('chapter-data')
  const links = Array.from(document.querySelectorAll<HTMLAnchorElement>('[data-chapter-link]'))
  if (!raw || links.length === 0) return

  let chapters: ChapterData[]
  try {
    chapters = JSON.parse(raw.textContent || '[]') as ChapterData[]
  } catch {
    return
  }

  const entries = chapters
    .map((chapter, index) => ({
      ...chapter,
      index,
      heading: document.getElementById(chapter.id),
      link: links[index],
    }))
    .filter((entry) => entry.heading && entry.link)

  if (entries.length === 0) return

  const railTitle = document.querySelector<HTMLElement>('[data-chapter-current]')
  const chapterNumber = document.querySelector<HTMLElement>('[data-chapter-number]')
  const coverCanvas = document.querySelector<HTMLCanvasElement>('[data-cover-canvas]')
  let activeIndex = -1

  const documentTop = (element: HTMLElement) => element.getBoundingClientRect().top + window.scrollY

  const fitCanvas = (canvas: HTMLCanvasElement) => {
    const ratio = Math.min(2, window.devicePixelRatio || 1)
    const rect = canvas.getBoundingClientRect()
    canvas.width = Math.max(1, Math.round(rect.width * ratio))
    canvas.height = Math.max(1, Math.round(rect.height * ratio))
    const context = canvas.getContext('2d')
    context?.setTransform(ratio, 0, 0, ratio, 0, 0)
    return { context, width: rect.width, height: rect.height }
  }

  const drawCover = () => {
    if (!coverCanvas) return
    const { context, width, height } = fitCanvas(coverCanvas)
    if (!context) return
    context.clearRect(0, 0, width, height)
    context.strokeStyle = 'rgba(34, 81, 255, .12)'
    context.fillStyle = 'rgba(34, 81, 255, .07)'
    context.lineWidth = 1

    const step = Math.max(42, width / 18)
    for (let x = step; x < width; x += step) {
      context.beginPath()
      context.moveTo(x, 0)
      context.lineTo(x, height)
      context.stroke()
    }
    for (let y = step; y < height; y += step) {
      context.beginPath()
      context.moveTo(0, y)
      context.lineTo(width, y)
      context.stroke()
    }

    const nodes = [
      [width * .66, height * .16, 76],
      [width * .83, height * .31, 54],
      [width * .71, height * .58, 92],
      [width * .91, height * .72, 64],
    ]
    for (const [x, y, size] of nodes) {
      context.fillRect(x - size / 2, y - size / 2, size, size)
      context.strokeStyle = 'rgba(34, 81, 255, .34)'
      context.strokeRect(x - size / 2, y - size / 2, size, size)
      context.beginPath()
      context.moveTo(x - size * .34, y)
      context.lineTo(x + size * .34, y)
      context.moveTo(x, y - size * .34)
      context.lineTo(x, y + size * .34)
      context.stroke()
    }
  }

  const render = (index: number) => {
    if (index === activeIndex) return
    activeIndex = index
    const entry = entries[index]

    links.forEach((link, linkIndex) => {
      if (linkIndex === index) {
        link.setAttribute('aria-current', 'location')
      } else {
        link.removeAttribute('aria-current')
      }
    })
    if (railTitle) railTitle.textContent = entry.title
    if (chapterNumber) chapterNumber.textContent = String(index + 1).padStart(2, '0')
  }

  const update = () => {
    const cover = document.getElementById('cover')
    document.body.classList.toggle('report-reading', window.scrollY >= Math.max(0, (cover?.offsetHeight || 0) - 2))
    const readingLine = Math.min(190, window.innerHeight * 0.28)
    let current = 0
    for (let index = 0; index < entries.length; index += 1) {
      if ((entries[index].heading as HTMLElement).getBoundingClientRect().top <= readingLine) {
        current = index
      }
    }
    render(current)

  }

  let scheduled = false
  const scheduleUpdate = () => {
    if (scheduled) return
    scheduled = true
    window.requestAnimationFrame(() => {
      update()
      scheduled = false
    })
  }

  links.forEach((link) => {
    link.addEventListener('click', (event) => {
      event.preventDefault()
      const id = link.getAttribute('href')?.slice(1)
      const target = id ? document.getElementById(id) : null
      if (!target) return
      window.scrollTo({
        top: documentTop(target) - 88,
        behavior: 'smooth',
      })
      target.setAttribute('tabindex', '-1')
      target.focus({ preventScroll: true })
    })
  })

  drawCover()
  update()
  window.addEventListener('scroll', scheduleUpdate, { passive: true })
  window.addEventListener('resize', () => {
    drawCover()
    scheduleUpdate()
  })
}
